/** Taille max fichier upload projet (1 Mo). */
export const U3121_UPLOAD_MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024;

/** Nombre max de fichiers par opération d'import (BL-0607). */
export const U3121_UPLOAD_MAX_FILES = 50;

export type U3121UploadFileValidationErrorKey = 'msgUploadFileTooLarge' | 'errorBU312105';

export type U3121UploadFileLineStatus = 'success' | 'error' | 'skipped';

export interface U3121UploadFileLineResult {
  fileName: string;
  status: U3121UploadFileLineStatus;
  projectId?: string | number;
  projetNumber?: string | null;
  errorMessage?: string;
}

export interface U3121PrepareUploadFilesResult {
  files: File[];
  rejectedOverMax: boolean;
  duplicateNames: string[];
}

export interface U3121AppendUploadFilesResult {
  files: File[];
  rejectedOverMax: boolean;
  duplicateNames: string[];
}

/** Valide extension et taille ; retourne une clé i18n ou null si OK. */
export function u3121ValidateUploadFile(file: File): U3121UploadFileValidationErrorKey | null {
  if (!file.name.toLowerCase().endsWith('.txt')) {
    return 'errorBU312105';
  }
  if (file.size > U3121_UPLOAD_MAX_FILE_SIZE_BYTES) {
    return 'msgUploadFileTooLarge';
  }
  return null;
}

function u3121PrepareUploadFilesRaw(raw: File[]): U3121PrepareUploadFilesResult {
  if (raw.length === 0) {
    return { files: [], rejectedOverMax: false, duplicateNames: [] };
  }

  if (raw.length > U3121_UPLOAD_MAX_FILES) {
    return { files: [], rejectedOverMax: true, duplicateNames: [] };
  }

  const sorted = [...raw].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const seen = new Set<string>();
  const files: File[] = [];
  const duplicateNames: string[] = [];

  for (const file of sorted) {
    const key = file.name.toLowerCase();
    if (seen.has(key)) {
      duplicateNames.push(file.name);
      continue;
    }
    seen.add(key);
    files.push(file);
  }

  return { files, rejectedOverMax: false, duplicateNames };
}

/**
 * Prépare la liste de fichiers : refus si &gt; max, tri alphabétique, dédoublonnage par nom (première occurrence conservée).
 */
export function u3121PrepareUploadFilesFromInput(fileList: FileList | null): U3121PrepareUploadFilesResult {
  if (!fileList || fileList.length === 0) {
    return { files: [], rejectedOverMax: false, duplicateNames: [] };
  }
  return u3121PrepareUploadFilesRaw(Array.from(fileList));
}

/**
 * Ajoute des fichiers à la file existante (BL-0645) : dédoublonnage, tri, plafond 50.
 * Si rejectedOverMax, retourne la file actuelle inchangée.
 */
/** incoming : copie immédiate (File[]) — ne pas passer un FileList après reset de l'input (référence live). */
export function u3121AppendUploadFilesToQueue(current: File[], incoming: File[] | null): U3121AppendUploadFilesResult {
  const raw = incoming ?? [];
  if (raw.length === 0) {
    return { files: [...current], rejectedOverMax: false, duplicateNames: [] };
  }

  const prepared = u3121PrepareUploadFilesRaw(raw);
  if (prepared.rejectedOverMax) {
    return { files: [...current], rejectedOverMax: true, duplicateNames: [] };
  }

  const queueKeys = new Set(current.map((f) => f.name.toLowerCase()));
  const duplicateNames = [...prepared.duplicateNames];
  const toAdd: File[] = [];

  for (const file of prepared.files) {
    const key = file.name.toLowerCase();
    if (queueKeys.has(key)) {
      duplicateNames.push(file.name);
      continue;
    }
    queueKeys.add(key);
    toAdd.push(file);
  }

  if (current.length + toAdd.length > U3121_UPLOAD_MAX_FILES) {
    return { files: [...current], rejectedOverMax: true, duplicateNames: [] };
  }

  const merged = [...current, ...toAdd].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return { files: merged, rejectedOverMax: false, duplicateNames };
}

/**
 * Fichier à afficher dans le volet d’aperçu (BL-0646) après changement de la file.
 * Conserve la sélection si le nom est encore présent ; sinon premier fichier de la file (ordre tri BL-0645).
 */
/**
 * Décode le contenu brut d’un sidecar .txt (souvent Windows-1252) pour l’aperçu BL-0646.
 * UTF-8 (avec ou sans BOM) si valide ; sinon repli windows-1252.
 */
export function u3121DecodeSidecarTextBytes(bytes: Uint8Array): string {
  if (bytes.length === 0) {
    return '';
  }

  let payload = bytes;
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    payload = bytes.subarray(3);
  }

  const utf8Lenient = new TextDecoder('utf-8', { fatal: false }).decode(payload);
  const hasReplacement = utf8Lenient.includes('\uFFFD');
  let utf8StrictOk = false;
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(payload);
    utf8StrictOk = true;
  } catch {
    utf8StrictOk = false;
  }

  if (utf8StrictOk && !hasReplacement) {
    return utf8Lenient;
  }

  return new TextDecoder('windows-1252').decode(payload);
}

/** Lit un fichier sidecar pour l’aperçu (encodage UTF-8 ou Windows-1252). */
export async function u3121ReadUploadSidecarPreviewText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  return u3121DecodeSidecarTextBytes(new Uint8Array(buffer));
}

export function u3121ResolveSelectedUploadFileName(previous: string | null, files: File[]): string | null {
  if (files.length === 0) {
    return null;
  }
  if (previous && files.some((f) => f.name === previous)) {
    return previous;
  }
  return files[0]?.name ?? null;
}

/** Numéros de projet des lignes importées en succès (BL-0608), ordre du récap, sans doublon. */
export function u3121ExtractSuccessProjetNumbersFromUploadResults(results: U3121UploadFileLineResult[]): string[] {
  const seen = new Set<string>();
  const numbers: string[] = [];
  for (const line of results) {
    if (line.status !== 'success') continue;
    const num = String(line.projetNumber ?? '').trim();
    if (!num || seen.has(num)) continue;
    seen.add(num);
    numbers.push(num);
  }
  return numbers;
}
